import {
  CALENDAR_MODES,
  readStoredCalendarMode,
  storeCalendarMode,
  type CalendarMode,
} from "@/pages/Dashboard/Calendar/calendarModeStorage";
import { isNativePlatform } from "@/utils/native";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useCallback, useState } from "react";

export { CALENDAR_MODES, type CalendarMode };

/**
 * Which calendar screen is shown. Deliberately separate from the store's
 * `calendarView` (day/week/month), which is sent to the API as the fetch range
 * — overloading it with a "list" value would leak presentation into the query.
 *
 * The `mode` param stays authoritative so a deep link wins; without one the
 * last mode the user picked is restored from storage. The setter always writes
 * the param, so every mounted consumer agrees on the mode regardless of what
 * each of them read from storage at mount.
 *
 * The list is a native-app screen only. On web the param is ignored and the
 * mode is always "grid", so the web dashboard behaves exactly as it did before
 * the mode existed.
 */
export function useCalendarMode() {
  const [storedMode] = useState(readStoredCalendarMode);
  const [urlMode, setUrlMode] = useQueryState(
    "mode",
    parseAsStringLiteral(CALENDAR_MODES),
  );

  const setMode = useCallback(
    (mode: CalendarMode) => {
      storeCalendarMode(mode);
      return setUrlMode(mode);
    },
    [setUrlMode],
  );

  const mode = urlMode ?? storedMode;

  return [isNativePlatform() ? mode : "grid", setMode] as const;
}
